import { Breadcrumb, Button } from "pepsico-ds";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { paths } from "../../../routes/paths";
import PageHeader from "../../common/PageStructure/PageHeader";
import PageWrapper from "../../common/PageStructure/PageWrapper";
import ToastNotification from "../../common/toastNotification/ToastNotification";
import ConfigurationModals from "./ConfigurationModals";

const ConfigurationEditPage = ({
  configKey,
  configType,
  headerText,
  description,
  breadcrumbPath,
  children,
  formProps,
}) => {
  const {
    isUpdated,
    isLoading,
    hasFieldErrors,
    confirmationModalOpen,
    discardModalOpen,
    toast,
    setConfirmationModalOpen,
    setDiscardModalOpen,
    setToast,
    handleSaveConfirmation,
  } = formProps;

  const navigate = useNavigate();

  const navigateToSystemSettings = () => {
    navigate(`/${paths.system_setting}`);
  };

  return (
    <PageWrapper>
      <Breadcrumb breadcrumbs={breadcrumbPath} />
      <PageHeader headerText={headerText} />
      <p className="$color-text-primary">{description}</p>

      <div className={`${configKey}-inputs-wrapper`}>
        <div className={`${configKey}-items`}>{children}</div>
        <div className="button-container">
          <Button
            size="medium"
            text={isUpdated ? "Discard" : "Back to System Setting"}
            variant="secondary"
            className="mr-2"
            onClick={() => {
              isUpdated ? setDiscardModalOpen(true) : navigateToSystemSettings();
            }}
          />
          <Button
            size="medium"
            text="Save"
            variant="primary"
            disabled={hasFieldErrors || !isUpdated}
            onClick={() => setConfirmationModalOpen(true)}
          />
        </div>
      </div>

      <ConfigurationModals
        confirmationModalOpen={confirmationModalOpen}
        discardModalOpen={discardModalOpen}
        setConfirmationModalOpen={setConfirmationModalOpen}
        setDiscardModalOpen={setDiscardModalOpen}
        isLoading={isLoading}
        handleSaveConfirmation={handleSaveConfirmation}
        navigateToSystemSettings={navigateToSystemSettings}
        configType={configType}
      />

      <ToastNotification
        {...toast}
        handleClose={() => {
          setToast({ ...toast, open: false });
          if (toast.navigateToSettings) {
            navigateToSystemSettings();
          }
        }}
      />
    </PageWrapper>
  );
};

ConfigurationEditPage.propTypes = {
  configKey: PropTypes.string.isRequired,
  configType: PropTypes.string.isRequired,
  headerText: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  breadcrumbPath: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      text: PropTypes.string.isRequired,
    })
  ).isRequired,
  children: PropTypes.node.isRequired,
  formProps: PropTypes.shape({
    isUpdated: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    hasFieldErrors: PropTypes.bool.isRequired,
    confirmationModalOpen: PropTypes.bool.isRequired,
    discardModalOpen: PropTypes.bool.isRequired,
    toast: PropTypes.shape({
      open: PropTypes.bool.isRequired,
      type: PropTypes.string,
      title: PropTypes.string,
      message: PropTypes.string,
      navigateToSettings: PropTypes.bool,
    }).isRequired,
    setConfirmationModalOpen: PropTypes.func.isRequired,
    setDiscardModalOpen: PropTypes.func.isRequired,
    setToast: PropTypes.func.isRequired,
    handleSaveConfirmation: PropTypes.func.isRequired,
  }).isRequired,
};

export default ConfigurationEditPage;
